/**
 * The last line of defence before anything reaches Google.
 *
 * The typed event map already stops a call site from inventing a parameter, but
 * types vanish at runtime: a cast, a JS call site, or a value interpolated from
 * server data all arrive here unchecked. So keys are filtered, values are
 * redacted, and GA4's own limits are enforced — every event, every time.
 */
import {
  MAX_NAME_LENGTH,
  MAX_PARAMS,
  MAX_VALUE_LENGTH,
  REDACTED,
} from './analytics.constants';
import type { AnalyticsOutgoingParams, AnalyticsScalar } from './analytics.types';

/**
 * Keys dropped outright. These routinely carry patient identifiers or free text
 * a user typed, neither of which belongs in analytics. Erring wide is
 * deliberate: a blocked useful key costs a report, an allowed patient name
 * costs a breach.
 */
const BLOCKED_PARAM_KEY =
  /patient|e_?mail|first_?name|last_?name|full_?name|display_?name|d_?o_?b|birth|phone|mobile_?number|mrn|ssn|social_?security|address|street|zip|postal|passw|secret|token|api_?key|auth|search|query|term|keyword|free_?text|note|comment/i;

const EMAIL_IN_TEXT = /[^\s/@]+@[^\s/@]+\.[^\s/@]+/g;
/** Long digit runs are phone numbers, record numbers and ids far more often than counts. */
const LONG_DIGIT_RUN = /\d{7,}/g;

/**
 * Stable, non-reversible token for an account id. A 64-bit FNV-1a fold is enough
 * here: the input is an opaque server-issued id, so the digest carries no name,
 * no email and nothing to look up. Kept synchronous on purpose — `crypto.subtle`
 * is async and would make every caller await an identity.
 */
export function pseudonymize(value: string): string {
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  for (let i = 0; i < value.length; i++) {
    hash ^= BigInt(value.charCodeAt(i));
    hash = (hash * prime) & 0xffffffffffffffffn;
  }
  return hash.toString(36);
}

/** Redact anything identifying out of a value, and hold it to GA4's limits. */
export function redactValue(value: unknown): AnalyticsScalar | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'boolean') return value;
  if (typeof value !== 'string') return undefined;

  const cleaned = value
    .replace(EMAIL_IN_TEXT, REDACTED)
    .replace(LONG_DIGIT_RUN, REDACTED)
    .trim();

  return cleaned ? cleaned.slice(0, MAX_VALUE_LENGTH) : undefined;
}

/** {@link redactValue} for a value already known to be text. */
export function redactText(value: string): string | undefined {
  const redacted = redactValue(value);
  return typeof redacted === 'string' ? redacted : undefined;
}

/** GA4 names are lower snake_case, start with a letter and carry no dangling separator. */
export function normalizeKey(key: string): string {
  return (
    key
      .trim()
      .replace(/[^A-Za-z0-9_]/g, '_')
      .replace(/_{2,}/g, '_')
      .replace(/^[^A-Za-z]+/, '')
      .toLowerCase()
      .slice(0, MAX_NAME_LENGTH)
      // Trailing separator, either from the source text or from the slice above.
      .replace(/_+$/, '')
  );
}

/** Filter a parameter bag: blocked keys dropped, values redacted, count capped. */
export function scrubParams(
  params: Readonly<Record<string, unknown>>,
): AnalyticsOutgoingParams {
  const scrubbed: Record<string, AnalyticsScalar> = {};

  for (const [rawKey, rawValue] of Object.entries(params)) {
    if (Object.keys(scrubbed).length >= MAX_PARAMS) break;
    if (BLOCKED_PARAM_KEY.test(rawKey)) continue;

    const key = normalizeKey(rawKey);
    if (!key) continue;

    const value = redactValue(rawValue);
    if (value === undefined) continue;

    scrubbed[key] = value;
  }

  return scrubbed;
}
